// Loading a year, and moving between years.
//
// ONE READ, and it is the whole screen: `GET /years/:year` arrives with the log
// counted, the schedules named and the reminders separated out, exactly as
// `GET /days/:day` arrives with a whole day. There is deliberately no second
// request behind a tile - a tile is not pressable, because looking INSIDE a day
// is the month screen's job and it already reaches every day of the year.
//
// WHICH YEAR IT IS is never this client's answer. `'this'` is what goes to the
// server on the first read, exactly as the month screen asks for `'this'` and
// the day screen asks for `'today'`, and the server resolves it in the user's
// own stored zone. Every move after that follows `previousYear` / `nextYear` off
// the payload while there is a payload to follow, and `addYears` off the year
// being asked for when there is not - because a year that could not be read must
// not take away the way back to one that could.
//
// The cache, the round-tracking and the failure handling below are the shape
// `month/useMonthScreen.ts` already holds, deliberately: it is the second screen
// in this client that is read on demand rather than at launch, and two different
// answers to "has this been read this round" is how two screens drift apart.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { IsoYear, YearResponse } from '../api/client.ts';
import { addYears, parseIsoDay } from '../iso-day.ts';
import { yearOfDay } from './progress-model.ts';

/** The literal the server resolves in the user's own stored zone. */
export const THIS_YEAR = 'this';

export type YearKey = IsoYear | typeof THIS_YEAR;

type Entry =
  | { status: 'loading' }
  /**
   * `refreshFailure` is set when a LATER read of the same year could not be
   * made. The payload stays, because it is the answer somebody is looking at
   * and it was right when it arrived; the reason travels with it so the screen
   * can say which of the two it is showing.
   */
  | { status: 'loaded'; payload: YearResponse; refreshFailure?: string }
  | { status: 'failed'; message: string };

/**
 * A read that did not answer, filed against what is already there.
 *
 * A FAILURE MUST NOT DESTROY THE WORKING THING - the same rule, and the same
 * function, the month screen states at length. Where nothing has ever loaded
 * this is the whole state; where something HAS, the payload stays and the reason
 * is said out loud beside it.
 */
function withProblem(
  cache: Record<string, Entry>,
  key: string,
  message: string,
): Record<string, Entry> {
  const current = cache[key];
  if (current?.status === 'loaded') {
    return { ...cache, [key]: { ...current, refreshFailure: message } };
  }
  return { ...cache, [key]: { status: 'failed', message } };
}

/**
 * Whether the payload answers the year it was asked for, and in a form this
 * screen can lay out.
 *
 * `firstDay` is the field the lattice cannot recover from: the blanks before the
 * 1st of January are its weekday, so a `date` column serialised as a timestamp
 * would shift every tile of the year by some number of columns and still look
 * exactly like a year grid. Both ends of the span are PARSED as calendar days
 * rather than compared as text, which is what makes that sentence true - a
 * timestamp shares a year's first four characters and sorts inside its span, so
 * neither of the checks beside this one can see it. Reported rather than drawn,
 * with the specifics going to whoever is building the server - the day and month
 * screens make this same check for this same reason, and `useMonthScreen` parses
 * its own `firstDay` in exactly this way.
 *
 * BOTH ENDS MUST BE IN THE YEAR THE PAYLOAD NAMES, which is what makes the span
 * ONE CALENDAR YEAR rather than merely an ordered pair of days. The lattice is
 * sized for a year - 371 cells against at most 366 days and six days of padding
 * - so a span running past December is one the grid cannot hold and the card's
 * own footnote would then misreport, telling somebody a single day is missing
 * while a week of them is. `lastDayOf` on the server is always `-12-31`, so this
 * refuses a defect rather than a shape anything here sends.
 */
function contractProblem(payload: YearResponse, requested: YearKey): string | null {
  if (
    parseIsoDay(payload.firstDay) === null ||
    parseIsoDay(payload.lastDay) === null ||
    payload.firstDay.slice(0, 4) !== payload.year ||
    payload.lastDay.slice(0, 4) !== payload.year ||
    payload.lastDay < payload.firstDay
  ) {
    console.warn(
      `Perfect Note: the year payload for ${JSON.stringify(payload.year)} spans ${JSON.stringify(payload.firstDay)} to ${JSON.stringify(payload.lastDay)}, which is not a year this screen can lay out.`,
    );
    return 'The server sent a year in a form this screen cannot read.';
  }
  if (requested !== THIS_YEAR && payload.year !== requested) {
    console.warn(
      `Perfect Note: asked for ${requested} and the server answered with ${payload.year}.`,
    );
    return 'The server sent a different year from the one this screen asked for.';
  }
  return null;
}

/**
 * Which round of reads a cache entry belongs to. ONE record answering both
 * halves of the rule, because two mechanisms for one invariant is how the month
 * screen's version of this drifted apart before.
 *
 * `revision` is the write round, including schedule creation that adds a current
 * zero card. `completions` is the tick round, and EVERY HISTORICAL FIGURE on this
 * screen is a count of completions - more so than the month, which at least draws
 * what is due as well - so the commonest write in the product is the one that
 * makes it wrong. `visit` is the trip to this screen the read was made on, and it
 * is null for a read that produced an ANSWER: an answer belongs to the write
 * round and is as good on the next trip as on this one.
 */
interface Round {
  revision: number;
  completions: number;
  visit: number | null;
}

function alreadyRead(record: Round | undefined, now: Round): boolean {
  if (record === undefined) return false;
  if (record.revision !== now.revision || record.completions !== now.completions) return false;
  return record.visit === null || record.visit === now.visit;
}

export interface ProgressScreenState {
  /** The year being looked at, once it has loaded. */
  view: YearResponse | null;
  /**
   * Which year is being ASKED FOR, which is a different question from which one
   * loaded: the bar names this one, so a 2025 that could not be read never sits
   * under a heading still reading 2026.
   *
   * Null only before anything at all has come back, which is the one state where
   * `'this'` has not been resolved by any payload.
   */
  year: IsoYear | null;
  /**
   * Where the arrows go. The PAYLOAD'S OWN whenever there is one, so the server
   * is the authority the moment it has spoken; `addYears` off the year being
   * asked for when there is not, because a year that failed to arrive must not
   * remove the way back to the one before it. Null exactly when `year` is.
   */
  previousYear: IsoYear | null;
  nextYear: IsoYear | null;
  /**
   * The year the server's own today falls in, or null until some payload has
   * said which day that is. Never a clock here.
   */
  thisYear: IsoYear | null;
  loading: boolean;
  /** Set only when the year being looked at could not be read AT ALL. */
  failure: string | null;
  /**
   * Set when the year ON SCREEN is still drawn but a later read of it could not
   * be made. Never both this and `failure`.
   */
  refreshFailure: string | null;
  /** Go to a year, or to the literal `'this'` and let the server say which. */
  go: (year: YearKey) => void;
  /** Ask for the year on screen again after it could not be read. */
  retry: () => void;
}

export interface ProgressScreenOptions {
  /**
   * Whether Progress is the screen on. Nothing is fetched while it is not.
   *
   * The DAY is read above the screens and stays read, because coming back to
   * Today must cost nothing. A year is not: it is a whole year of log rows for a
   * screen most mornings nobody opens. The cache lives in this hook rather than
   * in the screen, so leaving Progress and coming back still costs nothing -
   * what is deferred is the FIRST read, not every one after it.
   */
  active: boolean;
  /** Bumped by every write the sheets make. See `useMonthScreen`. */
  revision: number;
  /**
   * Bumped by every tick that reaches the server. Watched separately from
   * `revision` because the day screen's own read must NOT be re-sent on a tick -
   * that would undo the optimistic update - while everything on THIS screen is a
   * count of completions and is wrong the moment one is written.
   */
  completions: number;
  /** Raised by any 401. Never lowered - see `useDayScreen`. */
  onSignedOut: () => void;
}

export function useProgressScreen(options: ProgressScreenOptions): ProgressScreenState {
  const { active, revision, completions, onSignedOut } = options;

  const [key, setKey] = useState<YearKey>(THIS_YEAR);
  const [byYear, setByYear] = useState<Record<YearKey, Entry>>({});
  // The server's own today, kept from the last payload that carried one so the
  // bar can still say which year is THIS year while the year on screen has no
  // payload of its own. No clock is read here.
  const [today, setToday] = useState<string | null>(null);

  // Read through a ref so the effect below is not re-run by a caller that
  // re-renders: `onSignedOut` is a fresh closure every render.
  const signedOut = useRef(onSignedOut);
  signedOut.current = onSignedOut;

  // Both refs for the reason `useDayScreen` gives: StrictMode runs an effect
  // twice inside one commit, so a state flag saying "this is loading" has not
  // landed yet when the second run reads it.
  const loadedIn = useRef<Map<YearKey, Round>>(new Map());
  const inFlight = useRef<Set<YearKey>>(new Set());

  // WHICH TRIP TO THIS SCREEN THIS IS. Counted on the way OUT rather than on the
  // way in, so the number is fixed for the whole of a visit - see the same
  // counter in `useMonthScreen` for why both of those matter.
  const visit = useRef(0);

  useEffect(() => {
    if (active) return;
    visit.current += 1;
  }, [active]);

  const entry = byYear[key];

  useEffect(() => {
    if (!active || inFlight.current.has(key)) return;
    const round: Round = { revision, completions, visit: visit.current };
    if (entry !== undefined && alreadyRead(loadedIn.current.get(key), round)) return;

    inFlight.current.add(key);
    // Recorded on the way OUT rather than on the way back, so a read that failed
    // is a read this VISIT has had and the effect does not re-fire on it for
    // ever. Asking again without leaving is `retry`, which forgets the entry.
    loadedIn.current.set(key, round);
    // A year already READ stays on screen while it is replaced; one that FAILED
    // has nothing worth keeping.
    setByYear((cache) =>
      cache[key]?.status === 'failed' || cache[key] === undefined
        ? { ...cache, [key]: { status: 'loading' } }
        : cache,
    );

    void (async () => {
      try {
        const payload = await api.year(key);
        const problem = contractProblem(payload, key);
        if (problem !== null) {
          setByYear((cache) => withProblem(cache, key, problem));
          return;
        }
        const loaded: Entry = { status: 'loaded', payload };
        // AN ANSWER OUTLIVES THE VISIT THAT ASKED FOR IT. Filed under its own
        // name as well as under the literal, and both as fresh as each other, or
        // coming back to this year by name would fetch it a second time.
        const answered: Round = { revision, completions, visit: null };
        loadedIn.current.set(key, answered);
        loadedIn.current.set(payload.year, answered);
        // A `today` that is not a calendar date is not remembered at all, rather
        // than becoming a year name made of whatever four characters it starts
        // with. The month screen guards its own copy of this the same way.
        if (parseIsoDay(payload.today) !== null) setToday(payload.today);
        setByYear((cache) => ({ ...cache, [key]: loaded, [payload.year]: loaded }));
      } catch (error) {
        if (isSignedOut(error)) signedOut.current();
        // Filed against the entry even so, which is what stops this year being
        // asked for again: the effect re-fires for anything with no entry, and a
        // 401 that left the cache empty would send the same doomed request for
        // ever.
        setByYear((cache) => withProblem(cache, key, messageFor(error)));
      } finally {
        inFlight.current.delete(key);
      }
    })();
  }, [active, key, entry, revision, completions]);

  const view = entry?.status === 'loaded' ? entry.payload : null;

  // The year being ASKED FOR, named even when its payload never arrived: the
  // payload's own name first, then the key, and the literal resolved through the
  // server's `today` rather than through anything on this machine.
  const year: IsoYear | null =
    view?.year ?? (key === THIS_YEAR ? (today === null ? null : yearOfDay(today)) : key);

  const go = useCallback((year: YearKey) => {
    setKey(year);
  }, []);

  const retry = useCallback(() => {
    // Forgetting is what asks again: the effect above sends a request for
    // exactly the key that has no entry.
    setByYear((cache) => {
      if (cache[key] === undefined) return cache;
      const next = { ...cache };
      delete next[key];
      return next;
    });
  }, [key]);

  return useMemo(
    () => ({
      view,
      year,
      previousYear: view?.previousYear ?? (year === null ? null : addYears(year, -1)),
      nextYear: view?.nextYear ?? (year === null ? null : addYears(year, 1)),
      thisYear: today === null ? null : yearOfDay(today),
      loading: entry === undefined || entry.status === 'loading',
      failure: entry?.status === 'failed' ? entry.message : null,
      refreshFailure: entry?.status === 'loaded' ? (entry.refreshFailure ?? null) : null,
      go,
      retry,
    }),
    [view, year, today, entry, go, retry],
  );
}
